import classNames from "classnames";
import { useFormik } from "formik";
import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Badge from "../../Components/base/Badge";
import Card, { CardBody } from "../../Components/base/Card";
import Icon from "../../Components/base/Icon";
import Popovers from "../../Components/base/Popovers";
import DataTable from "../../Components/base/Table/DataTable";
import PaginationButtons, {
  PER_COUNT,
} from "../../Components/base/Table/Pagination/PaginationButtons";
import Tooltips from "../../Components/base/Tooltips";
import Button from "../../Components/base/forms/Button";
import Checks, { ChecksGroup } from "../../Components/base/forms/Checks";
import Dropdown, {
  DropdownMenu,
  DropdownToggle,
} from "../../Components/base/forms/Dropdown";
import FormGroup from "../../Components/base/forms/FormGroup";
import Input from "../../Components/base/forms/Input";
import SearchableSelect from "../../Components/base/forms/SearchableSelect";
import Page from "../../Components/layout/Page";
import PageWrapper from "../../Components/layout/PageWrapper";
import ThemeContext from "../../Contexts/themeContext";
import SubHeader, {
  SubHeaderLeft,
  SubHeaderRight,
  SubheaderSeparator,
} from "../../Core/FrameLayouts/SubHeader/SubHeader";
import useDarkMode from "../../Hooks/useDarkMode";
import useSaveFilterOnReload from "../../Hooks/useSaveFilterOnReload";
import useSortableData from "../../Hooks/useSortableData";
import { getFYEData } from "../../Services/FinancialYearEndServices";
import { initialsColumnWithColour } from "../../Utils/ColumnUtils";
import { dateFormat, showOrDash } from "../../Utils/Helpers";
import EditFinancialYearEnd from "./EditFinancialYearEnd";

const FinancialYearEndList = () => {
  const dispatch = useDispatch();
  const { darkModeStatus } = useDarkMode();
  const { appTheme } = useContext(ThemeContext);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(PER_COUNT["100"]);
  const [isArchive, setIsArchive] = useState(false);

  const [financialYearEndList, setFinancialYearEndList] = useState([]);
  const {
    fyeList,
    fyInternalReviewStatus,
    uniqueFYEDate,
    uniqueBuildingAccManager,
    uniqueAccountingMethod,
  } = useSelector((state) => state.financialYearEnd);

  const [fYEEditOffcanvas, setFYEEditOffcanvas] = useState(false);
  const [editItems, setEditItems] = useState({});

  const [isOpenCompleteModal, setIsOpenCompleteModal] = useState(false);

  /** Start Select Options */
  const uniqueAccountManagerOptions = uniqueBuildingAccManager.map(
    (account_manager) => ({
      value: account_manager,
      text: account_manager,
    }),
  );

  const uniqueFYEDateOptions = uniqueFYEDate.map((date) => ({
    value: date,
    text: dateFormat(date, "YYYY MMM"),
  }));

  const uniqueAccountingMethodOptions = uniqueAccountingMethod.map(
    (accounting_method) => ({
      value: accounting_method,
      text: accounting_method,
    }),
  );
  /** End Select Options */
  // The filtering does not work if you have already selected Page 2 or more of the display
  // at the bottom right of the screen. If you have selected a page, the filtering does not work.
  // TODO: Fix this Formik and filteredData code move to top of component with useEffect code.
  const [lastPageBeforeFilter, setLastPageBeforeFilter] = useState(null);

  const formik = useFormik({
    initialValues: {
      searchInput: "",
      fye: "",
      account_manager: "",
      accounting_method: "",
      audit_required_toggle: false,
    },
    onSubmit: (values) => { },
  });

  const filteredData = financialYearEndList.filter((item) => {
    // Combine all field values into a string
    const combinedFields = Object.values(item).join(" ").toLowerCase();
    // Search filter
    const search = formik.values.searchInput?.toLowerCase() || "";
    const matchesSearch = combinedFields.includes(search);
    // Status filter
    const fyeValue = formik.values.fye?.toLowerCase() || "";
    const matchesFYE =
      !fyeValue ||
      (item.financial_year_end &&
        dateFormat(item.financial_year_end, "YYYY MMM")
          .toLowerCase()
          .includes(dateFormat(fyeValue, "YYYY MMM").toLowerCase()));

    // Account Manager filter
    const accountManagerValue =
      formik.values.account_manager?.toLowerCase() || "";
    const matchesAccountManager =
      !accountManagerValue ||
      (item.account_manager_name &&
        item.account_manager_name.toLowerCase().includes(accountManagerValue));

    // Accounting Manager filter
    const accountingMethodValue =
      formik.values.accounting_method?.toLowerCase() || "";
    const matchesAccountingMethod =
      !accountingMethodValue ||
      (item.accounting_method &&
        item.accounting_method.toLowerCase().includes(accountingMethodValue));

    // If Toggle is off then show all else show only audit required = 1
    const auditRequiredValue = formik.values.audit_required_toggle;
    const matchesAuditRequired =
      !auditRequiredValue ||
      (item.audit_required && item.audit_required == auditRequiredValue);

    // Final return
    return (
      matchesSearch &&
      matchesFYE &&
      matchesAccountManager &&
      matchesAccountingMethod &&
      matchesAuditRequired
    );
  });

  const { items, requestSort, getClassNamesFor } =
    useSortableData(filteredData);

  const isFilterActive =
    formik.values.searchInput?.trim() !== "" ||
    formik.values.fye !== "" ||
    formik.values.account_manager !== "" ||
    formik.values.accounting_method !== "" ||
    formik.values.audit_required_toggle === 1;

  useEffect(() => {
    if (isFilterActive) {
      // Save current page before search starts
      if (lastPageBeforeFilter === null) {
        setLastPageBeforeFilter(currentPage);
      }
      setCurrentPage(1);
    } else {
      // Search cleared → restore page
      if (lastPageBeforeFilter !== null) {
        setCurrentPage(lastPageBeforeFilter);
        setLastPageBeforeFilter(null);
      }
    }
  }, [isFilterActive]);
  /** END FILTER AND SERCH When selected Page 2  */

  // Save filter state on reload
  useSaveFilterOnReload("FinancialYearEndFilters", formik);

  useEffect(() => {
    if (fyeList?.length === 0) {
      dispatch(getFYEData());
    }
  }, [appTheme]);

  useEffect(() => {
    if (isArchive) {
      setFinancialYearEndList(fyeList?.archived || []);
    } else {
      setFinancialYearEndList(fyeList?.allData || []);
    }
  }, [isArchive, fyeList]);

  /** Start Handle Methods */
  const handleOnClickEdit = (item) => {
    setEditItems(item);
    setFYEEditOffcanvas(!fYEEditOffcanvas);
  };

  const handleClearFilter = () => {
    formik.setFieldValue("fye", "");
    formik.setFieldValue("account_manager", "");
    formik.setFieldValue("accounting_method", "");
    localStorage.removeItem("FinancialYearEndFilters");
  };

  const handleClickOnArchive = () => {
    setIsArchive(!isArchive);
  };
  /** END Handle Methods */

  const RenderBuildingLink = ({ item }) => {
    return (
      <div>
        <Link
          onClick={() => {
            handleOnClickEdit(item);
          }}
          className={classNames({
            "link-dark": !darkModeStatus,
            "link-light": darkModeStatus,
          })}
        >
          {item.building_name} - {item.building_number}
        </Link>
      </div>
    );
  };

  const RenderReviewStatusColumn = ({ item }) => {
    const statusId = item?.BuildingDetailsFYEStatus?.id;

    const colorMap = {
      1: "--bs-primary",
      2: "--bs-primary",
      3: "var(--green)",
      4: "var(--orange)",
      5: "var(--yellow)",
      6: "var(--green)",
    };

    const backgroundColorMap = {
      1: "primary",
      2: "primary",
      3: "var(--green-light)",
      4: "var(--orange-light)",
      5: "var(--yellow-light)",
      6: "var(--green-light)",
    };

    const color = colorMap[statusId] || "info";
    const backgroundColor = backgroundColorMap[statusId] || "info";

    return (
      <Badge
        // color={color}
        isLight
        className="ms-2"
        style={{
          backgroundColor: `${backgroundColor} !important`,
          color: `${color} !important`,
          fontSize: "small",
        }}
      >
        {item?.BuildingDetailsFYEStatus?.status_name}
      </Badge>
    );
  };

  const columns = [
    {
      key: "financial_year_end",
      label: "FYE",
      isCenter: true,
      sortable: true,
      sticky: "left",
      render: (item) =>
        item.financial_year_end
          ? dateFormat(item.financial_year_end, "YYYY MMM")
          : "-",
    },
    {
      key: "building_name",
      label: "Building",
      sortable: true,
      sticky: "left",
      render: (item) => <RenderBuildingLink item={item} />,
    },
    {
      key: "account_manager_live",
      label: "Account Manager",
      sortable: true,
      render: (value, row) =>
        initialsColumnWithColour(value.account_manager_live),
    },
    {
      key: "lots_active_count",
      label: "Active Lots",
      sortable: true,
      isCenter: true,
    },
    {
      key: "audit_required",
      label: "Audit Required",
      sortable: true,
      isCenter: true,
      render: (value, row) =>
        value.audit_required == null ? (
          "-"
        ) : (
          <div
            style={{
              backgroundColor: value.audit_required ? "#FCF9DB" : "",
            }}
          >
            {value.audit_required ? "Yes" : "No"}
          </div>
        ),
    },
    {
      key: "auditor_name",
      label: "Auditor Name",
      sortable: true,
      render: (item) =>
        item.auditor_name &&
          item.auditor_name.toLowerCase().trim() != "no audit" ? (
          <div
            style={{
              backgroundColor: item.auditor_name ? "#FCF9DB" : "",
            }}
          >
            {item.auditor_name}
          </div>
        ) : (
          "-"
        ),
    },
    {
      key: "internal_review_status",
      label: "Status",
      sortable: true,
      render: (item) => {
        return item?.internal_review_status ? (
          <RenderReviewStatusColumn item={item} />
        ) : (
          "-"
        );
      },
    },
    {
      key: "internal_review_by",
      label: "Action By",
      sortable: true,
      isCenter: true,
      render: (item) =>
        item?.User ? item?.User?.first_name + " " + item?.User?.last_name : "-",
    },
    {
      key: "completed_date",
      label: "Accounts completion",
      sortable: true,
      isCenter: true,
      render: (item) =>
        item.completed_date ? dateFormat(item.completed_date) : "-",
    },
    {
      key: "sent_auditor_date",
      label: "Sent to Auditor",
      sortable: true,
      isCenter: true,
      render: (item) =>
        item.sent_auditor_date ? dateFormat(item.sent_auditor_date) : "-",
    },
    {
      key: "notes",
      label: "Notes/ Instructions",
      sortable: true,
      isCenter: true,
      render: (item) => showOrDash(item.notes),
    },
  ];

  const renderSubHeader = () => {
    const hasNonAuditFilters =
      formik.values.searchInput ||
      formik.values.fye ||
      formik.values.account_manager ||
      formik.values.accounting_method;
    return (
      <SubHeader>
        <SubHeaderLeft>
          <div className="d-flex align-items-center flex-grow-1">
            <label
              className="border-0 bg-transparent cursor-pointer me-0"
              htmlFor="searchInput"
            >
              <Icon
                iconName="Search"
                style={{ color: "primary", fontSize: "2x" }}
              />
            </label>
            <Input
              id="searchInput"
              type="search"
              className="border-0 shadow-none bg-transparent"
              placeholder="Search..."
              onChange={formik.handleChange}
              value={formik.values.searchInput}
            />
          </div>
        </SubHeaderLeft>
        <SubHeaderRight>
          <FormGroup
            label="Audit"
            className=" d-flex align-items-center"
            labelClassName="mb-0"
          >
            <ChecksGroup isInline>
              <Checks
                type="switch"
                id="audit_required_toggle"
                name="audit_required_toggle"
                className="mb-0 ps-3"
                checked={formik.values.audit_required_toggle === 1} // make sure it's true/false
                onChange={(e) =>
                  formik.setFieldValue(
                    "audit_required_toggle",
                    e.target.checked ? 1 : 0,
                  )
                }
                style={{
                  width: "3rem",
                  height: "1.50rem",
                  marginLeft: "0.125rem",
                }}
              />
            </ChecksGroup>
          </FormGroup>
          <SubheaderSeparator />
          <Tooltips
            title={isArchive ? "Archived List" : "Unarchive List"}
            placement="bottom"
          >
            <FormGroup
              label="Archived"
              className=" d-flex align-items-center"
              labelClassName="mb-0"
            >
              <ChecksGroup isInline>
                <Checks
                  type="switch"
                  id="archive_toggle"
                  name="archive_toggle"
                  className="mb-0 ps-3"
                  checked={isArchive} // make sure it's true/false
                  onChange={(e) => handleClickOnArchive()}
                  style={{
                    width: "3rem",
                    height: "1.50rem",
                    marginLeft: "0.125rem",
                  }}
                />
              </ChecksGroup>
            </FormGroup>
          </Tooltips>
          <SubheaderSeparator />
          <Dropdown>
            <DropdownToggle hasIcon={false}>
              <Button
                icon="Funnel"
                color="dark"
                isLight
                className="btn-only-icon position-relative"
                aria-label="Filter"
              >
                {financialYearEndList.length !== filteredData.length &&
                  hasNonAuditFilters && (
                    <Popovers desc="Filtering applied" trigger="hover">
                      <span className="position-absolute top-0 start-100 translate-middle badge border border-light rounded-circle bg-danger p-2">
                        <span className="visually-hidden">
                          there is filtering
                        </span>
                      </span>
                    </Popovers>
                  )}
              </Button>
            </DropdownToggle>
            <DropdownMenu isAlignmentEnd size="xxl">
              <div className="container py-2">
                <div className="row g-3">
                  <FormGroup label="FYE" className="col-md-12">
                    <SearchableSelect
                      name="fye"
                      list={uniqueFYEDateOptions}
                      placeholder="Select FYE"
                      onChange={formik.handleChange}
                      value={formik.values.fye}
                      search={true}
                    />
                  </FormGroup>
                  <FormGroup label="Account Manager" className="col-md-12">
                    <SearchableSelect
                      name="account_manager"
                      list={uniqueAccountManagerOptions}
                      placeholder="Select Account Manager"
                      onChange={formik.handleChange}
                      value={formik.values.account_manager}
                      search={true}
                    />
                  </FormGroup>
                  <FormGroup label="Accounting Method" className="col-md-12">
                    <SearchableSelect
                      name="accounting_method"
                      list={uniqueAccountingMethodOptions}
                      placeholder="Select Accounting Method"
                      onChange={formik.handleChange}
                      value={formik.values.accounting_method}
                      search={true}
                    />
                  </FormGroup>
                  <FormGroup
                    label=""
                    className="col-md-6 offset-md-6 d-flex justify-content-end"
                  >
                    <Button color="secondary" onClick={handleClearFilter}>
                      Clear
                    </Button>
                  </FormGroup>
                </div>
              </div>
            </DropdownMenu>
          </Dropdown>
        </SubHeaderRight>
      </SubHeader>
    );
  };

  return (
    <PageWrapper
      headerTitle={"Financial Year End List"}
      title={"Financial Year End"}
    >
      {renderSubHeader()}
      <Page container="fluid">
        <div className="row h-100">
          <div className="col-12">
            <Card stretch>
              <CardBody isScrollable className="table-responsive">
                <DataTable
                  columns={columns}
                  data={items}
                  perPage={perPage}
                  currentPage={currentPage}
                  requestSort={requestSort}
                  getClassNamesFor={getClassNamesFor}
                  darkModeStatus={darkModeStatus}
                  stickyHeader={true}
                />
              </CardBody>
              <PaginationButtons
                data={items}
                label={"items"}
                setCurrentPage={setCurrentPage}
                currentPage={currentPage}
                perPage={perPage}
                setPerPage={setPerPage}
              />
            </Card>
          </div>
        </div>
      </Page>
      <EditFinancialYearEnd
        itemData={editItems}
        fyInternalReviewStatus={fyInternalReviewStatus}
        setIsOpen={setFYEEditOffcanvas}
        setIsOpenCompleteModal={setIsOpenCompleteModal}
        isOpen={fYEEditOffcanvas}
      />
    </PageWrapper>
  );
};

export default FinancialYearEndList;
